import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import {
  Figure,
  NameLink,
  PersonScopeCard,
  TD,
  TD_MUTED,
  TD_NUM,
  TH,
  TH_NUM,
  THEAD,
  directorHref,
  partyHref,
  useMoney,
} from '@/pages/cvm/shared';
import { fetchCvmCase } from '@/services/cvm';
import type { CvmCaseDetail, CvmCaseWalk } from '@/services/types';

/**
 * One judged case: the extrato's own walk.
 *
 * Head, then the decision clause item by item with its vote mode, then the
 * sanction rows exactly as the parser attributed them (each with the clause it
 * was cut from, so a reader can check it), then every director's recorded
 * position, and the tracks that name the same process: settlement decisions,
 * the CRSFN acórdão, the register's status events, the judgments this one
 * cites. The walk is refused at RECORD scope when the document has no text
 * layer or no located clause -- the corpus supports it, this document does not.
 */
export function CvmCaseDetailPage() {
  const { key = '' } = useParams();
  const { t, i18n } = useTranslation(['cvm', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cvm', 'case', key],
    queryFn: () => fetchCvmCase(key),
    enabled: key.length > 0,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const money = useMoney();

  if (isPending) return <SkeletonLoader rows={10} />;
  if (error) return <ErrorMessage message={error.message} onRetry={() => void refetch()} />;

  const { head } = data;
  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <p className="text-xs text-muted-foreground">
          <Link to="/cvm/cases" className="underline-offset-2 hover:underline">
            {t('cvm:case.back')}
          </Link>
        </p>
        <h1 className="text-lg font-semibold text-foreground">
          {t('cvm:case.title', { process: head.process_number })}
        </h1>
        <p className="text-sm text-muted-foreground">{head.ementa ?? t('cvm:case.noEmenta')}</p>
      </header>

      <ExplainerCard i18nKey="cvm:case.explainer" />
      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-head>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Figure label={t('cvm:case.head.judgment')} value={head.judgment_date ?? '—'} />
          <Figure
            label={t('cvm:case.head.session')}
            value={head.session_number ? String(head.session_number) : '—'}
            hint={t(`cvm:sources.${data.source_kind}`, {
              defaultValue: data.source_kind,
            })}
          />
          <div className="rounded-md border bg-muted/30 p-3">
            <div className="text-xs text-muted-foreground">{t('cvm:case.head.relator')}</div>
            <div className="mt-1 text-base font-semibold text-foreground">
              <NameLink name={head.relator} href={directorHref(head.relator_slug)} />
            </div>
          </div>
          <Figure label={t('cvm:case.head.rite')} value={head.rite ?? '—'} />
          <Figure label={t('cvm:case.head.unit')} value={head.originating_unit ?? '—'} />
          <Figure
            label={t('cvm:case.head.accused')}
            value={number.format(head.accused_count)}
            hint={t('cvm:case.head.keyClass', { cls: head.key_class })}
          />
        </dl>
        <p className="mt-3 text-xs text-muted-foreground">
          {t('cvm:case.head.presiding')}: {data.presiding ?? '—'}
          {' · '}
          {t('cvm:case.head.participants')}:{' '}
          {data.participants.length ? data.participants.join('; ') : '—'}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {data.appeal_notice
            ? t('cvm:case.head.appealNotice', {
                days: data.appeal_deadline_days ?? '—',
              })
            : t('cvm:case.head.noAppealNotice')}
          {data.cvm_ex_officio_appeal ? ` · ${t('cvm:case.head.exOfficio')}` : ''}
          {' · '}
          <a
            href={data.source_url}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline-offset-2 hover:underline"
          >
            {t('cvm:case.head.source')}
          </a>
          {data.diario_date ? ` (${t('cvm:case.head.diario', { date: data.diario_date })})` : ''}
        </p>
      </section>

      <section className="rounded-lg border bg-card p-4" data-walk>
        <h2 className="mb-2 text-sm font-semibold text-foreground">{t('cvm:case.walk.title')}</h2>
        <Answer answer={data.walk} help={t('cvm:case.walk.help')} scope="record">
          {(walk: CvmCaseWalk) => (
            <div className="space-y-2">
              {walk.vote_mode_overall ? (
                <p className="text-xs text-muted-foreground">
                  {t('cvm:case.walk.overall')}:{' '}
                  {t(`cvm:voteModes.${walk.vote_mode_overall}`, {
                    defaultValue: walk.vote_mode_overall,
                  })}
                </p>
              ) : null}
              <ol className="space-y-2">
                {walk.items.map((item) => (
                  <li key={item.seq} className="rounded-md border bg-muted/30 p-3 text-sm">
                    <div className="flex flex-wrap items-baseline gap-2 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {item.label ?? t('cvm:case.walk.item', { seq: item.seq })}
                      </span>
                      {item.vote_mode ? (
                        <span>
                          {t(`cvm:voteModes.${item.vote_mode}`, {
                            defaultValue: item.vote_mode,
                          })}
                        </span>
                      ) : null}
                      {item.outcome_code ? (
                        <span>
                          {t(`cvm:outcomes.${item.outcome_code}`, {
                            defaultValue: item.outcome_code,
                          })}
                        </span>
                      ) : null}
                      <span>
                        {t('cvm:case.walk.sanctions', {
                          count: item.sanctions,
                        })}
                      </span>
                    </div>
                    <p className="mt-1 whitespace-pre-line text-foreground">{item.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </Answer>
      </section>

      <section className="rounded-lg border bg-card p-4" data-sanctions>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('cvm:case.sanctions.title')}
        </h2>
        {data.sanctions.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('cvm:case.sanctions.none')}</p>
        ) : (
          <div className="overflow-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className={THEAD}>
                <tr>
                  <th className={TH}>{t('cvm:case.sanctions.item')}</th>
                  <th className={TH}>{t('cvm:case.sanctions.party')}</th>
                  <th className={TH}>{t('cvm:case.sanctions.type')}</th>
                  <th className={TH_NUM}>{t('cvm:case.sanctions.amount')}</th>
                  <th className={TH}>{t('cvm:case.sanctions.provision')}</th>
                  <th className={TH}>{t('cvm:case.sanctions.evidence')}</th>
                </tr>
              </thead>
              <tbody>
                {data.sanctions.map((s, index) => (
                  <tr key={index} className="border-t align-top">
                    <td className={TD_NUM}>{s.item_seq}</td>
                    <td className={TD}>
                      <NameLink name={s.party ?? s.party_name_raw} href={partyHref(s.party_slug)} />
                      {s.entity_type ? (
                        <div className="text-xs text-muted-foreground">
                          {t(`cvm:entityTypes.${s.entity_type}`, {
                            defaultValue: s.entity_type,
                          })}
                        </div>
                      ) : null}
                    </td>
                    <td className={TD}>
                      {t(`cvm:sanctionTypes.${s.sanction_type}`, {
                        defaultValue: s.sanction_type,
                      })}
                      {s.duration_raw ? (
                        <div className="text-xs text-muted-foreground">{s.duration_raw}</div>
                      ) : null}
                    </td>
                    <td className={TD_NUM}>
                      {money(s.amount_brl)}
                      {s.amount_attribution ? (
                        <div className="text-xs font-normal text-muted-foreground">
                          {t(`cvm:attributions.${s.amount_attribution}`, {
                            defaultValue: s.amount_attribution,
                          })}
                        </div>
                      ) : null}
                    </td>
                    <td className={TD_MUTED}>
                      <div className="text-foreground">{s.provision ?? '—'}</div>
                      {s.provisions.length > 1 ? <div>{s.provisions.join('; ')}</div> : null}
                      {s.basis.length ? (
                        <div>
                          {t('cvm:case.sanctions.basis')}: {s.basis.join('; ')}
                        </div>
                      ) : null}
                    </td>
                    <td className={TD_MUTED}>
                      <span title={s.evidence_span}>
                        {s.evidence_span.length > 160
                          ? `${s.evidence_span.slice(0, 160)}…`
                          : s.evidence_span}
                      </span>
                      {!s.evidence_verified ? (
                        <div className="text-amber-600">{t('cvm:case.sanctions.unverified')}</div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-lg border bg-card p-4" data-votes>
        <h2 className="mb-2 text-sm font-semibold text-foreground">{t('cvm:case.votes.title')}</h2>
        {data.votes.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('cvm:case.votes.none')}</p>
        ) : (
          <div className="overflow-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className={THEAD}>
                <tr>
                  <th className={TH}>{t('cvm:case.votes.voter')}</th>
                  <th className={TH}>{t('cvm:case.votes.position')}</th>
                  <th className={TH}>{t('cvm:case.votes.scope')}</th>
                  <th className={TH}>{t('cvm:case.votes.target')}</th>
                  <th className={TH}>{t('cvm:case.votes.evidence')}</th>
                </tr>
              </thead>
              <tbody>
                {data.votes.map((v, index) => (
                  <tr key={index} className="border-t align-top">
                    <td className={TD}>
                      <NameLink
                        name={v.voter_canonical ?? v.voter}
                        href={directorHref(v.voter_slug)}
                      />
                      {v.voter_canonical && v.voter_canonical !== v.voter ? (
                        <div className="text-xs text-muted-foreground">{v.voter}</div>
                      ) : null}
                    </td>
                    <td className={TD}>
                      {t(`cvm:positions.${v.position}`, {
                        defaultValue: v.position,
                      })}
                    </td>
                    <td className={TD_MUTED}>
                      {t(`cvm:voteScopes.${v.scope}`, {
                        defaultValue: v.scope,
                      })}
                      {v.item_seq ? ` · ${t('cvm:case.walk.item', { seq: v.item_seq })}` : ''}
                    </td>
                    <td className={TD_MUTED}>{v.target ?? '—'}</td>
                    <td className={TD_MUTED}>{v.evidence_span ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Tracks data={data} />

      <PersonScopeCard answer={data.person_scope} />
      <FieldGuide i18nKey="cvm:case.guide" />
    </div>
  );
}

function Tracks({ data }: { data: CvmCaseDetail }) {
  const { t, i18n } = useTranslation(['cvm']);
  const number = new Intl.NumberFormat(i18n.language);
  const money = useMoney();
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-lg border bg-card p-4" data-settlements>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('cvm:case.settlements.title')}
        </h2>
        {data.settlements.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('cvm:case.settlements.none')}</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {data.settlements.map((s, index) => (
              <li key={index} className="rounded-md border bg-muted/30 p-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <NameLink name={s.party} href={partyHref(s.party_slug)} />
                  <span className="text-xs text-muted-foreground">{s.decided_at ?? '—'}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {t(`cvm:settlementOutcomes.${s.outcome}`, {
                    defaultValue: s.outcome,
                  })}
                  {' · '}
                  {money(s.amount_brl)}
                  {s.amount_is_split ? ` (${t('cvm:case.settlements.split')})` : ''}
                  {s.is_unanimous == null
                    ? ''
                    : ` · ${s.is_unanimous ? t('cvm:case.settlements.unanimous') : t('cvm:case.settlements.majority')}`}
                  {s.relator ? ` · ${s.relator}` : ''}
                  {' · '}
                  <a
                    href={s.decision_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    {t('cvm:case.settlements.decision')}
                  </a>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-lg border bg-card p-4" data-appeals>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('cvm:case.appeals.title')}
        </h2>
        {data.appeals.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('cvm:case.appeals.none')}</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {data.appeals.map((a, index) => (
              <li key={index} className="rounded-md border bg-muted/30 p-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <NameLink name={a.party} href={partyHref(a.party_slug)} />
                  <span className="text-xs text-muted-foreground">
                    {a.session_number
                      ? t('cvm:case.appeals.session', {
                          number: a.session_number,
                        })
                      : '—'}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {t(`cvm:appealRoles.${a.role}`, { defaultValue: a.role })}
                  {' · '}
                  {a.outcome
                    ? t(`cvm:appealOutcomes.${a.outcome}`, {
                        defaultValue: a.outcome,
                      })
                    : t('cvm:case.appeals.unread')}
                  {a.sanction_after
                    ? ` · ${t(`cvm:sanctionAfter.${a.sanction_after}`, { defaultValue: a.sanction_after })}`
                    : ''}
                  {a.fine_after_appeal_brl ? ` · ${money(a.fine_after_appeal_brl)}` : ''}
                  {a.relator ? ` · ${a.relator}` : ''}
                  {a.acordao_number ? ` · ${a.acordao_number}` : ''}
                  {' · '}
                  <a
                    href={a.page_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    {t('cvm:case.appeals.page')}
                  </a>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-lg border bg-card p-4" data-register>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('cvm:case.register.title')}
        </h2>
        {data.register_phase == null && data.register_events.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('cvm:case.register.none')}</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              {t('cvm:case.register.phase')}: {data.register_phase ?? '—'}
            </p>
            <ul className="mt-2 space-y-1 text-xs">
              {data.register_events.map((e, index) => (
                <li key={index} className="flex flex-wrap justify-between gap-2 border-t pt-1">
                  <span className="text-foreground">{e.name}</span>
                  <span className="text-muted-foreground">
                    {e.situacao ?? '—'} · {e.situacao_date ?? '—'}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className="rounded-lg border bg-card p-4" data-citations>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('cvm:case.citations.title')}
        </h2>
        <p className="text-xs text-muted-foreground">
          {t('cvm:case.citations.citedBy', { count: data.cited_by })}
        </p>
        {/* Cited keys are what the text spells; not every one is a case in
            Gold, so they are chips and not links -- a link to a case that is
            not here would land on an error page. */}
        {data.citations.length ? (
          <ul className="mt-2 flex flex-wrap gap-2 text-xs">
            {data.citations.map((c) => (
              <li key={c} className="rounded border px-2 py-0.5 text-foreground">
                {c}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">{t('cvm:case.citations.none')}</p>
        )}
        {data.other_judgments.length ? (
          <p className="mt-2 text-xs text-muted-foreground">
            {t('cvm:case.citations.otherJudgments', {
              count: data.other_judgments.length,
              n: number.format(data.other_judgments.length),
            })}
            : {data.other_judgments.join('; ')}
          </p>
        ) : null}
      </section>
    </div>
  );
}
